import { useState, useEffect, useCallback, useMemo } from "react";
import { Input } from "@/components/ui/input";
import { Check, Search } from "lucide-react";
import { cn } from "@/lib/utils";

// Search configuration
const DEBOUNCE_DELAY = 300;
const MIN_SEARCH_LENGTH = 1;

const ProductSearch = ({ products, onSelect, className }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedTerm, setDebouncedTerm] = useState("");

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedTerm(searchTerm.trim().toLowerCase());
    }, DEBOUNCE_DELAY);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Memoized search results with advanced filtering
  const filteredResults = useMemo(() => {
    // Only search if term meets minimum length
    if (debouncedTerm.length < MIN_SEARCH_LENGTH) return [];

    // Advanced multi-field search
    return (
      products
        ?.filter(
          (product) =>
            // Check if product is active (status 1)
            (product.status === 1) &&
            // Match product title
            (product.title?.toLowerCase().includes(debouncedTerm) ||
              // Match product ID
              product.id?.toString().includes(debouncedTerm) ||
              // Match SKU (inside variations)
              (product.variations &&
                typeof product.variations === 'object' &&
                product.variations.sku?.toLowerCase().includes(debouncedTerm))
            )
        )
        .slice(0, 10) || []
    ); // Limit to 10 results
  }, [debouncedTerm, products]);

  // Handle product selection
  const handleSelect = useCallback(
    (product) => {
      onSelect(product);
      setSearchTerm("");
    },
    [onSelect]
  );

  return (
    <div className={cn("w-full relative", className)}>
      <div className="relative">
        <Input
          type="text"
          placeholder="Search products by name, ID..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10"
        />
        <Search
          className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground"
          size={20}
        />
      </div>

      {/* Search Results Dropdown */}
      {searchTerm.length >= MIN_SEARCH_LENGTH && (
        <div className="absolute z-50 w-full mt-1 bg-background border shadow-lg max-h-[300px] overflow-auto">
          {/* No Results State */}
          {filteredResults.length === 0 && (
            <div className="p-2 text-sm text-muted-foreground text-center">
              No products found
            </div>
          )}

          {/* Results List */}
          {filteredResults.map((product) => { // Use map block for logic
            const isOutOfStock = (product.stockQuantity || 0) <= 0;
            return (
              <div
                key={product.id}
                className={cn(
                  "flex items-center justify-between w-full px-3 py-2 transition-colors border-b last:border-0",
                  isOutOfStock ? "opacity-50 cursor-not-allowed bg-gray-50 dark:bg-slate-800" : "cursor-pointer hover:bg-accent"
                )}
                onClick={() => {
                  if (!isOutOfStock) handleSelect(product);
                }}
                role="button"
                tabIndex={isOutOfStock ? -1 : 0}
              >
                <div className="flex items-center gap-3">
                  <Check className="w-4 h-4 opacity-0" />
                  <div>
                    <div className="font-medium flex items-center gap-2">
                      {product.title}
                      {isOutOfStock && <span className="text-[10px] bg-red-100 text-red-600 px-1.5 py-0.5 rounded-full font-bold">OUT OF STOCK</span>}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      ID: {product.id} | Stock: <span className={isOutOfStock ? "text-red-500 font-bold" : "text-green-600"}>{product.stockQuantity || 0}</span>
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-semibold">GHS {parseFloat(product.current_price || 0).toFixed(2)}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ProductSearch;
